import { UsersApiHelper, UsersApiHelperForMember } from "api/ApiHelper";
import Axios from "axios";
import { FileExcelIcon, FilePdfIcon, FinancialDocumentsIcon } from "components/Icons/Icons";
import fileDownload from "js-file-download";

import { ExcelIcon, PdfIcon } from "../components/Icons/Icons";

export const CONTENT_TYPE_EXCEL_XLS = "application/vnd.ms-excel";
export const CONTENT_TYPE_EXCEL_XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
export const CONTENT_TYPE_PDF = "application/pdf";

export const uploadDocument = async (_financialDocument, _file, globalState) => {
  const _signedUrl = await UsersApiHelper.updateFinancialDocument(_financialDocument, globalState);

  await Axios.create().request({
    method: "PUT",
    url: _signedUrl.url,
    data: await _file?.arrayBuffer(),
    timeout: 3600 * 1000,
    maxContentLength: Infinity,
    maxBodyLength: Infinity,
    // onUploadProgress: (progress) => {
    //     // console.log("Progress", progress);
    // },
  });
};

export const downloadDocument = async (_financialDocument, globalState) => {
  if (_financialDocument?.content_type) {
    const _signedUrl = await UsersApiHelper.getFinancialDocument(_financialDocument, globalState);
    if (_signedUrl?.url) {
      return downloadDocumentByUrl(_signedUrl.url, _financialDocument);
    }
  } else {
    return false;
  }
};

export const downloadDocumentByUrl = async (_url, _financialDocument) => {
  var _extension = getFileTypeExtension(_financialDocument);

  try {
    const _blob = await Axios.create().get(_url, {
      responseType: "blob",
      maxContentLength: Infinity,
      maxBodyLength: Infinity,
    });
    fileDownload(_blob.data, _financialDocument.document_type_name + _extension);
    return true;
  } catch (err) {
    console.warn(err);
    throw "File download was not available. Select another file.";
  }
};

export const getFileIcon = (file) => {
  switch (file?.type || file?.content_type) {
    case CONTENT_TYPE_PDF:
      return <PdfIcon data-testid="file-pdf-icon" sx={{ fontSize: "3rem", color: "colors.textAlertRed" }} />;
    case CONTENT_TYPE_EXCEL_XLS:
    case CONTENT_TYPE_EXCEL_XLSX:
      return <ExcelIcon data-testid="file-excel-icon" sx={{ fontSize: "3rem", color: "colors.textDarkGreen" }} />;
    default:
      return <ExcelIcon data-testid="file-excel-icon" sx={{ fontSize: "3rem", color: "colors.textDarkGreen" }} />;
  }
};

export const getFileTypeName = (file) => {
  switch (file?.type || file?.content_type) {
    case CONTENT_TYPE_PDF:
      return "PDF";
    case CONTENT_TYPE_EXCEL_XLS:
    case CONTENT_TYPE_EXCEL_XLSX:
      return "Excel";
    default:
      return "Other";
  }
};

export const getFileTypeExtension = (file) => {
  switch (file?.type || file?.content_type) {
    case CONTENT_TYPE_PDF:
      return ".pdf";
    case CONTENT_TYPE_EXCEL_XLS:
      return ".xls";
    case CONTENT_TYPE_EXCEL_XLSX:
      return ".xlsx";
    default:
      return "";
  }
};

export const getTruncatedFilename = (filename, maxLength = 30) => {
  if (!filename) return "";
  if (filename.length <= maxLength) return filename;

  const ellipsis = "...";
  const extIndex = filename.lastIndexOf(".");
  const extension = extIndex !== -1 ? filename.slice(extIndex) : "";
  const base = extIndex !== -1 ? filename.slice(0, extIndex) : filename;

  if (extension.length + ellipsis.length >= maxLength) {
    return filename.slice(0, maxLength - ellipsis.length) + ellipsis;
  }

  const baseLength = maxLength - extension.length - ellipsis.length;
  const truncatedBase = baseLength > 0 ? base.slice(0, baseLength) : "";

  return truncatedBase + ellipsis + extension;
};

/**
 * Fetch structured document file data from the Files/File endpoint.
 * Routed through UsersAPI's Datanac-authenticated relay (which authorizes
 * agent->producer UserGrants before forwarding to the underlying S3-backed
 * file metadata logic).
 *
 * @param {string} documentId - The document ID
 * @param {object} globalState - Global state containing tokens
 * @param {boolean} isWritable - If true, requests a writable URL for content updates
 * @returns {Promise<object>} - Object containing metadata and content JSON
 */
export const getDocumentStructuredData = async (documentId, isWritable = false, globalState) => {
  try {
    const params = {
      DocumentId: documentId,
      DoIncludeMetadata: true,
      DoIncludeChunks: true,
      DoIncludeDocument: true,
      DoIncludeContent: true,
      IsWritable: Boolean(isWritable),
      TenantID: import.meta.env.VITE_RGCHAT_TENANTID_TILLEY,
      UserID: globalState?.producer_token || globalState?.agent_token,
      SessionID: "00000000-0000-0000-0000-000000000000",
      AccessKey: "00000000-0000-0000-0000-000000000000",
      // producer_token + agent_token drive the UsersAPI relay's
      // agent->producer UserGrant authorization.
      producer_token: globalState?.producer_token,
      agent_token: globalState?.agent_token,
    };

    const response = await UsersApiHelperForMember.http_get(
      `${import.meta.env.VITE_DATANAC_USERS_API_URL_BASE}/Files/File`,
      params
    );

    const data = response?.data;
    if (!data) {
      console.warn("Files/File returned no data");
      return null;
    }

    // If contentUrl exists, fetch the actual JSON content (presigned S3 URL).
    if (data?.contentUrl) {
      try {
        const contentResponse = await fetch(data.contentUrl);
        if (contentResponse.ok) {
          const contentJson = await contentResponse.json();
          return {
            metadata: data,
            content: contentJson,
          };
        }
      } catch (contentError) {
        console.warn("Error fetching content from contentUrl:", contentError);
      }
    }

    return {
      metadata: data,
      content: null,
    };
  } catch (error) {
    console.error("Error fetching document structured data:", error);
    return null;
  }
};
